import { HOME_PAGE_ROUTE } from '@constants/routes';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

/**
 * Switches the active studio (a sibling company under the same parent).
 * Navigates home and clears per-studio state so data never leaks between
 * studios, then applies the new selection on the next tick to avoid a flicker
 * during the route change.
 */
export const useStudioSwitch = () => {
  const { selectedCompanyStudios, setSelectedCompany } = useCompanySelector();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const switchStudio = useCallback(
    (companyIdentifier: string) => {
      const company = selectedCompanyStudios.find(
        (studio) => studio.identifier === companyIdentifier,
      );
      if (!company) {
        return;
      }

      navigate(HOME_PAGE_ROUTE);

      // Reset app state on studio change to avoid data leakage between studios
      dispatch(mainActions.resetStates({ shouldResetSelectedCompany: false }));

      // Delay the state update until after the route change to avoid a flicker
      setTimeout(() => {
        setSelectedCompany(company);
      }, 100);
    },
    [selectedCompanyStudios, setSelectedCompany, navigate, dispatch],
  );

  return { switchStudio };
};
